import React from 'react';
import { Col, Row } from 'reactstrap';
import OrderTicketDetail from '@/model/OrderTicketDetail';
import OrderTicket from '@/model/OrderTicket';
import PaymentType from '@/model/PaymentType';
import PaymentStatus from '@/model/PaymentStatus';
import formatValueToCurrency from '@/helpers/common/mask';
import dayjs from 'dayjs';
import { updateMask } from '@/helpers/masks/cpf';
import { FaFilePdf, FaQrcode } from 'react-icons/fa';
import { toast } from 'react-toastify';

interface TicketDetailContentProps {
  detail: OrderTicketDetail;
  orderTicket?: OrderTicket;
}

export const TicketDetailContent: React.FC<TicketDetailContentProps> = ({
  detail,
  orderTicket,
}) => {
  const fromPaymentType = (type?: PaymentType, installments?: number): string => {
    if (type === undefined || type === null) return '';
    switch (type) {
      case PaymentType.CREDIT_CARD:
        return `Cartão de crédito ${installments || 1}X`;
      case PaymentType.TWO_CREDIT_CARDS:
        return `Dois cartões ${installments || 1}X`;
      case PaymentType.DEBIT_CARD:
        return 'Débito';
      case PaymentType.PIX:
        return 'PIX';
      case PaymentType.BANK_SLIP:
        return 'Boleto';
      case PaymentType.MONEY:
        return 'Dinheiro';
      default:
        return '';
    }
  };

  const fromPaymentStatus = (status?: PaymentStatus): string => {
    if (status === undefined || status === null) return '';
    switch (status) {
      case PaymentStatus.APPROVED:
        return 'Aprovado';
      case PaymentStatus.DISAPPROVED:
        return 'Reprovado';
      case PaymentStatus.AWAITING_PAYMENT:
        return 'Aguardando Pagamento';
      case PaymentStatus.CANCELED:
        return 'Cancelado';
      case PaymentStatus.CANCELLATION_ANALYSIS:
        return 'Aguardando análise do cancelamento';
      default:
        return '';
    }
  };

  return (
    <>
      {detail && (
        <div style={{ padding: '10px' }}>
          <Row>
            <Col md={6} style={{ marginBottom: '15px' }}>
              <label
                className="input-label m-0 font-weight-bold"
                style={{ color: '#777', fontSize: '12px' }}
              >
                Código do ingresso (ID)
              </label>
              <div style={{ fontSize: '14px', marginTop: '4px' }}>{detail.id}</div>
            </Col>
            <Col md={6} style={{ marginBottom: '15px' }}>
              <label
                className="input-label m-0 font-weight-bold"
                style={{ color: '#777', fontSize: '12px' }}
              >
                Data e hora da utilização
              </label>
              <div style={{ fontSize: '14px', marginTop: '4px' }}>
                {detail.useDate
                  ? dayjs(detail.useDate).locale('pt-br').format('DD/MM/YYYY [às] HH:mm')
                  : 'Não utilizado'}
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6} style={{ marginBottom: '15px' }}>
              <label
                className="input-label m-0 font-weight-bold"
                style={{ color: '#777', fontSize: '12px' }}
              >
                Nome do comprador
              </label>
              <div style={{ fontSize: '14px', marginTop: '4px' }}>{detail.clientName}</div>
            </Col>
            <Col md={6} style={{ marginBottom: '15px' }}>
              <label
                className="input-label m-0 font-weight-bold"
                style={{ color: '#777', fontSize: '12px' }}
              >
                CPF do comprador
              </label>
              <div style={{ fontSize: '14px', marginTop: '4px' }}>
                {updateMask(detail.clientCPF)}
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6} style={{ marginBottom: '15px' }}>
              <label
                className="input-label m-0 font-weight-bold"
                style={{ color: '#777', fontSize: '12px' }}
              >
                PDV
              </label>
              <div style={{ fontSize: '14px', marginTop: '4px' }}>{detail.pdvName}</div>
            </Col>
            <Col md={6} style={{ marginBottom: '15px' }}>
              <label
                className="input-label m-0 font-weight-bold"
                style={{ color: '#777', fontSize: '12px' }}
              >
                Pagamento na maquininha
              </label>
              <div style={{ fontSize: '14px', marginTop: '4px' }}>{detail.posName || 'Nenhum'}</div>
            </Col>
          </Row>

          {orderTicket && (
            <>
              <hr style={{ margin: '15px 0', borderColor: '#eee' }} />
              <Row>
                <Col md={6} style={{ marginBottom: '15px' }}>
                  <label
                    className="input-label m-0 font-weight-bold"
                    style={{ color: '#777', fontSize: '12px' }}
                  >
                    Data da Venda
                  </label>
                  <div style={{ fontSize: '14px', marginTop: '4px' }}>
                    {orderTicket.saleDate
                      ? dayjs(orderTicket.saleDate).locale('pt-br').format('DD/MM/YYYY [às] HH:mm')
                      : '-'}
                  </div>
                </Col>
                <Col md={6} style={{ marginBottom: '15px' }}>
                  <label
                    className="input-label m-0 font-weight-bold"
                    style={{ color: '#777', fontSize: '12px' }}
                  >
                    Valor do Ingresso
                  </label>
                  <div style={{ fontSize: '14px', marginTop: '4px', fontWeight: 'bold' }}>
                    {formatValueToCurrency(`${orderTicket.saleValue}`).masked}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6} style={{ marginBottom: '15px' }}>
                  <label
                    className="input-label m-0 font-weight-bold"
                    style={{ color: '#777', fontSize: '12px' }}
                  >
                    Forma de Pagamento
                  </label>
                  <div style={{ fontSize: '14px', marginTop: '4px' }}>
                    {orderTicket.payments && orderTicket.payments.length > 0
                      ? orderTicket.payments
                          .map(p => fromPaymentType(p.paymentType, p.installments))
                          .join(', ')
                      : '-'}
                  </div>
                </Col>
                <Col md={6} style={{ marginBottom: '15px' }}>
                  <label
                    className="input-label m-0 font-weight-bold"
                    style={{ color: '#777', fontSize: '12px' }}
                  >
                    Status da Compra
                  </label>
                  <div style={{ fontSize: '14px', marginTop: '4px' }}>
                    {fromPaymentStatus(orderTicket.orderPaymentStatus)}
                  </div>
                </Col>
              </Row>

              {orderTicket.payments && orderTicket.payments.some(p => p.transaction) && (
                <Row>
                  <Col md={12} style={{ marginBottom: '15px' }}>
                    <label
                      className="input-label m-0 font-weight-bold"
                      style={{ color: '#777', fontSize: '12px' }}
                    >
                      Código de Transação
                    </label>
                    <div style={{ fontSize: '14px', marginTop: '4px', fontFamily: 'monospace' }}>
                      {orderTicket.payments
                        .filter(p => p.transaction)
                        .map(p => p.transaction)
                        .join(', ')}
                    </div>
                  </Col>
                </Row>
              )}

              <Row>
                <Col md={12} style={{ marginBottom: '15px' }}>
                  <label
                    className="input-label m-0 font-weight-bold"
                    style={{ color: '#777', fontSize: '12px' }}
                  >
                    Links e Downloads
                  </label>
                  <div className="d-flex mt-2" style={{ gap: '10px' }}>
                    {orderTicket.pdfUrl ? (
                      <a
                        href={orderTicket.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm d-flex align-items-center"
                        style={{
                          gap: '5px',
                          borderColor: '#3CAFC8',
                          color: '#fff',
                          backgroundColor: '#3CAFC8',
                        }}
                      >
                        <FaFilePdf /> Baixar PDF
                      </a>
                    ) : (
                      <span
                        className="text-muted"
                        style={{ fontSize: '12px', alignSelf: 'center' }}
                      >
                        PDF indisponível
                      </span>
                    )}

                    {orderTicket.qrCode ? (
                      <button
                        type="button"
                        className="btn btn-sm d-flex align-items-center"
                        style={{
                          gap: '5px',
                          borderColor: '#3CAFC8',
                          color: '#fff',
                          backgroundColor: '#3CAFC8',
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(orderTicket.qrCode || '');
                          toast.success('Token do QR Code copiado!');
                        }}
                      >
                        <FaQrcode /> Copiar Token QR Code
                      </button>
                    ) : (
                      <span
                        className="text-muted ml-3"
                        style={{ fontSize: '12px', alignSelf: 'center' }}
                      >
                        QR Code indisponível
                      </span>
                    )}
                  </div>
                </Col>
              </Row>
            </>
          )}
        </div>
      )}
    </>
  );
};
